/**
 * Reading a newsletter body's markup the way a mail client's HTML parser
 * does, for `checkNewsletterBody` (`email-newsletter.ts`;
 * email-newsletter-editions, 2026-10-18 review).
 *
 * The first checks scraped attributes with regexes, and ordinary valid HTML
 * walked past them: an unquoted value (`<img src=https://…>`), a `>` inside an
 * earlier quoted value, a value that spans lines, a character reference
 * (`&#106;avascript:`), an `on…` handler. So the checks now read a token
 * stream instead: every tag with every attribute, the content of every
 * `<style>` block, and which tags sit inside an HTML comment. Outlook renders
 * the markup inside its conditional comments (`<!--[if mso]> … <![endif]-->`),
 * so a comment's content is read as markup too, and marked as such.
 *
 * It is a tokenizer, not a parser: it builds no tree and repairs nothing.
 * Markup it cannot read the way a browser would (an unclosed tag, quote or
 * comment) is reported as a problem, so a check never passes markup it did
 * not see. Every loop moves forward, so a 100 KB body is read in linear time.
 *
 * Names are folded as the HTML parser folds them, ASCII A–Z only. A Unicode
 * lower-casing changes some characters' length (`İ` lower-cases to two UTF-16
 * units), which shifted every name read after one, so a body could hide a
 * `<script>` behind them and an ordinary "İstanbul" broke the tags that
 * followed (2026-10-19 review).
 *
 * Pure, no imports: the dashboard page bundles it through `email-newsletter.ts`.
 */

/** One attribute as written: its name ASCII-lower-cased, its value undecoded. */
export interface HtmlAttribute {
  readonly name: string;
  /** The raw value, character references NOT decoded; `null` for a bare attribute. */
  readonly value: string | null;
  /** The value was written in quotes (a bare attribute is not). */
  readonly quoted: boolean;
}

/** One start or end tag. */
export interface HtmlTag {
  /** ASCII-lower-cased (`img`, `v:fill`). */
  readonly name: string;
  readonly closing: boolean;
  /** Every attribute as written, a repeated one included (the HTML parser keeps the first). */
  readonly attributes: ReadonlyArray<HtmlAttribute>;
  /** The tag sits inside an `<!-- … -->` comment. */
  readonly inComment: boolean;
  /**
   * Text a reader sees came between the tag before and this one: a character
   * other than whitespace once character references are decoded (`&nbsp;`
   * alone is not). A comment's content is not text; inside a comment this
   * counts within it.
   */
  readonly textBefore: boolean;
  /**
   * Of that text, some is words of its own: visible once every `{{ … }}`
   * placeholder in it is taken out. A placeholder is filled per recipient,
   * and may fill in as nothing (a member with no phone), so it is not a
   * word a reader is sure to see (2026-10-23).
   */
  readonly wordsBefore: boolean;
  /**
   * When the text has no words of its own, the placeholders it is made of,
   * as written, so a caller that knows one always fills can count it as
   * words. Empty when it has words, or no text.
   */
  readonly placeholdersBefore: ReadonlyArray<string>;
}

/** One `<style>` block's content. */
export interface HtmlStyleBlock {
  readonly css: string;
  readonly inComment: boolean;
}

export interface HtmlScan {
  readonly tags: ReadonlyArray<HtmlTag>;
  readonly styleBlocks: ReadonlyArray<HtmlStyleBlock>;
  /** Markup that could not be read, in words (`has an unclosed <a> tag`). */
  readonly problems: ReadonlyArray<string>;
}

/**
 * Elements whose content the HTML parser reads as text, not markup, up to
 * their own end tag (raw text and RCDATA). A newsletter may use only `style`
 * and `title` of these; the rest are read this way so that what follows them
 * is read as a browser would, and the check refuses the tag itself.
 */
const RAW_TEXT_ELEMENTS: ReadonlySet<string> = new Set([
  "script",
  "style",
  "title",
  "textarea",
  "xmp",
  "iframe",
  "noembed",
  "noframes",
  "noscript",
]);

const isWhitespace = (ch: string | undefined): boolean =>
  ch === " " || ch === "\t" || ch === "\n" || ch === "\r" || ch === "\f";

const isAsciiLetter = (ch: string | undefined): boolean =>
  ch !== undefined && ((ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z"));

/**
 * `s` with A–Z lower-cased and every other character as it was: the HTML
 * parser's fold for tag and attribute names. Every character keeps its
 * length, so an index into `s` is the same index into the result.
 */
function asciiLowercase(s: string): string {
  return s.replace(/[A-Z]+/gu, (run) => run.toLowerCase());
}

/** Whether a run of text between tags has a character a reader sees. */
function hasVisibleText(raw: string): boolean {
  return /\S/u.test(raw) && /\S/u.test(decodeHtmlReferences(raw).text);
}

/**
 * A `{{ … }}` placeholder, as the flow interpolator and the newsletter's
 * tokens write one. A `{{` that this does not close is text a reader sees.
 */
const PLACEHOLDER_RE = /\{\{[^{}]*\}\}/gu;

/**
 * Where `</name` (any case) followed by whitespace, `/` or `>` starts, from
 * `from`; -1 if nowhere. `lower` is `html` ASCII-lower-cased once by the caller.
 */
function findEndTag(html: string, lower: string, name: string, from: number): number {
  let at = lower.indexOf(`</${name}`, from);
  while (at >= 0) {
    const after = html[at + 2 + name.length];
    if (after === undefined || isWhitespace(after) || after === "/" || after === ">") return at;
    at = lower.indexOf(`</${name}`, at + 1);
  }
  return -1;
}

interface ScanSink {
  tags: HtmlTag[];
  styleBlocks: HtmlStyleBlock[];
  problems: string[];
}

function scanInto(html: string, inComment: boolean, out: ScanSink): void {
  const lower = asciiLowercase(html);
  const n = html.length;
  // Where the next `-->` and `--!>` start, from the last search on (-1: none
  // left; -2: not looked for yet). Each search starts past the last one, so
  // a body of many comments is still read in linear time.
  let dashEnd = -2;
  let bangEnd = -2;
  // Visible text since the last tag (comments and declarations are not text).
  let textSeen = false;
  // Of it, words of its own (not a placeholder); while there are none, the
  // placeholders it is made of.
  let wordsSeen = false;
  let placeholders: string[] = [];
  let i = 0;
  while (i < n) {
    const lt = html.indexOf("<", i);
    if (lt < 0) return;
    if (lt > i && !wordsSeen) {
      const run = html.slice(i, lt);
      if (hasVisibleText(run)) {
        textSeen = true;
        if (hasVisibleText(run.replace(PLACEHOLDER_RE, ""))) {
          wordsSeen = true;
          placeholders = [];
        } else {
          // One at a time: a long body can hold more than a spread may pass.
          for (const placeholder of run.match(PLACEHOLDER_RE) ?? []) placeholders.push(placeholder);
        }
      }
    }
    i = lt;

    if (html.startsWith("<!--", i)) {
      // The parser reads `<!-->` and `<!--->` as empty comments, and ends a
      // comment at the first `-->` or `--!>`.
      if (html.startsWith(">", i + 4)) {
        i += 5;
        continue;
      }
      if (html.startsWith("->", i + 4)) {
        i += 6;
        continue;
      }
      if (dashEnd !== -1 && dashEnd < i + 4) dashEnd = html.indexOf("-->", i + 4);
      if (bangEnd !== -1 && bangEnd < i + 4) bangEnd = html.indexOf("--!>", i + 4);
      if (dashEnd < 0 && bangEnd < 0) {
        // Inside a comment this is Outlook's own idiom, cut by the comment's
        // end: `<!--[if !mso]><!-- -->`, which every marketing design opens
        // its non-Outlook picture with (2026-10-23). What follows it is still
        // read as markup, so nothing in it passes unseen.
        if (inComment) {
          i += 4;
          continue;
        }
        out.problems.push("has an unclosed <!-- comment");
        return;
      }
      const bang = bangEnd >= 0 && (dashEnd < 0 || bangEnd < dashEnd);
      const contentEnd = bang ? bangEnd : dashEnd;
      scanInto(html.slice(i + 4, contentEnd), true, out);
      i = contentEnd + (bang ? 4 : 3);
      continue;
    }

    const next = html[i + 1];
    if (next === "!" || next === "?") {
      // A doctype, `<![endif]>`, or a bogus comment: each ends at the first `>`.
      const end = html.indexOf(">", i + 2);
      if (end < 0) {
        // Inside a comment this is Outlook's own idiom, cut by the comment's
        // end: `<!--[if !mso]><!-->` and `<!--<![endif]-->`.
        if (!inComment) out.problems.push("has an unclosed <! declaration");
        return;
      }
      i = end + 1;
      continue;
    }

    let j = i + 1;
    let closing = false;
    if (html[j] === "/") {
      closing = true;
      j += 1;
    }
    if (!isAsciiLetter(html[j])) {
      if (closing) {
        // `</` with no name: a bogus comment up to the first `>`.
        const end = html.indexOf(">", j);
        if (end < 0) {
          out.problems.push("has an unclosed </ tag");
          return;
        }
        i = end + 1;
        continue;
      }
      // A `<` that opens no tag is text, and a word of its own.
      textSeen = true;
      wordsSeen = true;
      placeholders = [];
      i += 1;
      continue;
    }

    // The tag name runs to whitespace, `/` or `>`.
    let k = j;
    while (k < n && !isWhitespace(html[k]) && html[k] !== "/" && html[k] !== ">") k += 1;
    const name = lower.slice(j, k);

    const attributes: HtmlAttribute[] = [];
    let p = k;
    let closed = false;
    while (p < n) {
      const ch = html[p];
      if (isWhitespace(ch) || ch === "/") {
        p += 1;
        continue;
      }
      if (ch === ">") {
        closed = true;
        p += 1;
        break;
      }
      // The attribute name runs to whitespace, `/`, `>` or `=`; a leading `=`
      // belongs to the name, as in the HTML parser.
      let q = p + (ch === "=" ? 1 : 0);
      while (q < n && !isWhitespace(html[q]) && html[q] !== "/" && html[q] !== ">" && html[q] !== "=") {
        q += 1;
      }
      const attrName = lower.slice(p, q);
      p = q;
      while (p < n && isWhitespace(html[p])) p += 1;
      let value: string | null = null;
      let quoted = false;
      if (html[p] === "=") {
        p += 1;
        while (p < n && isWhitespace(html[p])) p += 1;
        const quote = html[p];
        if (quote === '"' || quote === "'") {
          const end = html.indexOf(quote, p + 1);
          if (end < 0) {
            out.problems.push(`has an unclosed quoted attribute on a <${name}> tag`);
            return;
          }
          value = html.slice(p + 1, end);
          quoted = true;
          p = end + 1;
        } else {
          let e = p;
          while (e < n && !isWhitespace(html[e]) && html[e] !== ">") e += 1;
          value = html.slice(p, e);
          p = e;
        }
      }
      attributes.push({ name: attrName, value, quoted });
    }
    if (!closed) {
      out.problems.push(`has an unclosed <${name}> tag`);
      return;
    }
    out.tags.push({
      name,
      closing,
      attributes,
      inComment,
      textBefore: textSeen,
      wordsBefore: wordsSeen,
      placeholdersBefore: placeholders,
    });
    textSeen = false;
    wordsSeen = false;
    placeholders = [];
    i = p;

    if (!closing && RAW_TEXT_ELEMENTS.has(name)) {
      const end = findEndTag(html, lower, name, i);
      if (end < 0) {
        out.problems.push(`has an unclosed <${name}> element`);
        return;
      }
      if (name === "style") out.styleBlocks.push({ css: html.slice(i, end), inComment });
      i = end;
    }
  }
}

/** Every tag and `<style>` block in `html`, with what could not be read. */
export function scanHtml(html: string): HtmlScan {
  const out: ScanSink = { tags: [], styleBlocks: [], problems: [] };
  scanInto(html, false, out);
  return out;
}

/**
 * The named character references a newsletter's attributes plausibly carry,
 * plus the ones that spell a URL scheme or a CSS function (`&colon;`,
 * `&lpar;`). Names are case-sensitive, as in HTML (`&Colon;` is not `:`);
 * any other `&name;` is reported as unknown rather than guessed at.
 */
const NAMED_REFERENCES: ReadonlyMap<string, string> = new Map([
  ["amp", "&"],
  ["AMP", "&"],
  ["lt", "<"],
  ["LT", "<"],
  ["gt", ">"],
  ["GT", ">"],
  ["quot", '"'],
  ["QUOT", '"'],
  ["apos", "'"],
  ["nbsp", "\u00a0"],
  ["colon", ":"],
  ["semi", ";"],
  ["comma", ","],
  ["period", "."],
  ["sol", "/"],
  ["bsol", "\\"],
  ["lpar", "("],
  ["rpar", ")"],
  ["lsqb", "["],
  ["lbrack", "["],
  ["rsqb", "]"],
  ["rbrack", "]"],
  ["lcub", "{"],
  ["lbrace", "{"],
  ["rcub", "}"],
  ["rbrace", "}"],
  ["num", "#"],
  ["percnt", "%"],
  ["equals", "="],
  ["plus", "+"],
  ["excl", "!"],
  ["quest", "?"],
  ["commat", "@"],
  ["dollar", "$"],
  ["lowbar", "_"],
  ["ast", "*"],
  ["midast", "*"],
  ["grave", "`"],
  ["Hat", "^"],
  ["verbar", "|"],
  ["vert", "|"],
  ["Tab", "\t"],
  ["NewLine", "\n"],
]);

/**
 * Decode a value's character references: numeric (`&#106;`, `&#x6A;`, with
 * or without the `;`, any number of digits) and the named ones above (with
 * their `;`). `unknownNamed` is true when a `&name;` this reader does not
 * know is left in, so a check that must see the decoded text can refuse
 * instead of guessing. A legacy reference written without its `;` (`&amp`)
 * is left as written, so it can only make a URL look less like an allowed
 * one, never more.
 */
export function decodeHtmlReferences(value: string): {
  readonly text: string;
  readonly unknownNamed: boolean;
} {
  let unknownNamed = false;
  const text = value.replace(
    /&(?:#(\d+);?|#[xX]([0-9a-fA-F]+);?|([A-Za-z][A-Za-z0-9]*);)/gu,
    (whole, dec: string | undefined, hex: string | undefined, named: string | undefined) => {
      if (dec !== undefined || hex !== undefined) {
        const code = dec !== undefined ? Number.parseInt(dec, 10) : Number.parseInt(hex ?? "", 16);
        // As the parser does: 0, beyond Unicode or a surrogate is U+FFFD.
        if (!(code > 0 && code <= 0x10ffff) || (code >= 0xd800 && code <= 0xdfff)) return "\ufffd";
        return String.fromCodePoint(code);
      }
      const known = named === undefined ? undefined : NAMED_REFERENCES.get(named);
      if (known !== undefined) return known;
      unknownNamed = true;
      return whole;
    },
  );
  return { text, unknownNamed };
}

/**
 * A URL attribute's value as the URL parser sees it: tabs and line breaks
 * removed from anywhere, control characters and spaces trimmed from the
 * ends. (`java\tscript:` is `javascript:` to a browser.)
 */
export function normalizeUrlValue(decoded: string): string {
  const s = decoded.replace(/[\t\n\r]/gu, "");
  // Trimmed by hand: a `[…]+$` pattern backtracks quadratically on a long
  // run of spaces that does not end the value.
  let start = 0;
  let end = s.length;
  while (start < end && s.charCodeAt(start) <= 0x20) start += 1;
  while (end > start && s.charCodeAt(end - 1) <= 0x20) end -= 1;
  return s.slice(start, end);
}

/**
 * Whether the link `tags[at]` opens has something to click: text, or a
 * picture, before the next `<a>` or `</a>` (the HTML parser ends a link at
 * either). What sits inside a comment does not count: a browser does not
 * show it.
 *
 * A `{{ … }}` placeholder counts as text unless the caller passes
 * `placeholderFills`: then text counts only when it has words of its own, or
 * a placeholder `placeholderFills` says always fills in as something. A
 * template, whose placeholders a member's details fill in later, is read
 * that way; an email already filled in has none left to ask about.
 */
export function linkHasContent(
  tags: ReadonlyArray<HtmlTag>,
  at: number,
  placeholderFills?: (placeholder: string) => boolean,
): boolean {
  for (let t = at + 1; t < tags.length; t += 1) {
    const tag = tags[t];
    if (tag === undefined || tag.inComment) continue;
    const text =
      placeholderFills === undefined
        ? tag.textBefore
        : tag.wordsBefore || tag.placeholdersBefore.some(placeholderFills);
    if (text) return true;
    if (tag.name === "a") return false;
    if (!tag.closing && tag.name === "img") return true;
  }
  return false;
}

/** The links a document has to one address ({@link countLinks}). */
export interface LinkCount {
  /** `<a>` tags outside any comment whose `href` is the address. */
  readonly links: number;
  /** Of those, the ones with words or a picture to click ({@link linkHasContent}). */
  readonly clickable: number;
}

/**
 * The links among `tags` (one {@link scanHtml} read) whose `href` `isTarget`
 * accepts. It is handed the value as a mail client reads it (character
 * references decoded, then {@link normalizeUrlValue}) and as written. A link
 * is read as the HTML parser reads one: an `<a>` start tag outside any
 * comment, and its FIRST `href` (the parser drops a repeated attribute). An
 * unquoted value holding `{{` is skipped, because a filled token's spaces
 * would end it early.
 *
 * `checkNewsletterBody` counts its `{{unsubscribe_url}}` links with it, and
 * `email-unsubscribe.ts` a flow email's unsubscribe link (2026-10-23,
 * marketing-unsubscribe-guarantee — moved here from `email-newsletter.ts`
 * unchanged, so the two cannot read a link differently). `placeholderFills`
 * is handed to {@link linkHasContent}.
 */
export function countLinks(
  tags: ReadonlyArray<HtmlTag>,
  isTarget: (url: string, raw: string) => boolean,
  placeholderFills?: (placeholder: string) => boolean,
): LinkCount {
  let links = 0;
  let clickable = 0;
  for (const [at, tag] of tags.entries()) {
    if (tag.name !== "a" || tag.closing || tag.inComment) continue;
    const href = tag.attributes.find((a) => a.name === "href");
    if (href === undefined) continue;
    const raw = href.value ?? "";
    if (!href.quoted && raw.includes("{{")) continue;
    if (!isTarget(normalizeUrlValue(decodeHtmlReferences(raw).text), raw)) continue;
    links += 1;
    if (linkHasContent(tags, at, placeholderFills)) clickable += 1;
  }
  return { links, clickable };
}

/**
 * The URLs in a `srcset` value, read the way the HTML parser reads image
 * candidates: each URL runs to whitespace (commas inside it included), a
 * trailing comma ends a candidate, and a candidate's descriptors run to the
 * next comma outside parentheses.
 */
export function srcsetUrls(value: string): ReadonlyArray<string> {
  const urls: string[] = [];
  const n = value.length;
  let i = 0;
  while (i < n) {
    while (i < n && (isWhitespace(value[i]) || value[i] === ",")) i += 1;
    if (i >= n) break;
    let j = i;
    while (j < n && !isWhitespace(value[j])) j += 1;
    let url = value.slice(i, j);
    i = j;
    if (url.endsWith(",")) {
      // Trimmed by hand, as in `normalizeUrlValue`: `/,+$/` retried from
      // every comma of a long run inside the URL (2026-10-19 review).
      let end = url.length;
      while (end > 0 && url.charCodeAt(end - 1) === 0x2c) end -= 1;
      url = url.slice(0, end);
    } else {
      let depth = 0;
      while (i < n) {
        const c = value[i];
        i += 1;
        if (c === "(") depth += 1;
        else if (c === ")") depth = Math.max(0, depth - 1);
        else if (c === "," && depth === 0) break;
      }
    }
    if (url.length > 0) urls.push(url);
  }
  return urls;
}
